import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { Cpu, Eye, EyeOff, Lock, Mail, ShieldCheck, User, Wallet } from 'lucide-react'
import { AuthLayout, Field } from '@/components/AuthLayout'
import { SIGNED_IN_HOME } from '@/services/platform'
import { useAuth } from '@/store/auth'

/**
 * Sign in and registration.
 *
 * <p>One component serving two routes, the way the rest of the suite does it:
 * `/login` and `/signup` are separate addresses so either can be linked and
 * bookmarked, but they are the same form with a different mode.
 *
 * <p>Where SSO is configured this is the SSO's own sign-in/sign-up, submitted
 * through its API rather than a redirect, so the form stays the one this app
 * already has.
 */
export function LoginPage() {
  const [params] = useSearchParams()
  const { status, error, pendingVerificationEmail, login, register, resendVerification, clearError } = useAuth()
  const { pathname } = useLocation()
  const creating = pathname === '/signup'

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  // Moving between the two routes should not carry the previous mode's error.
  useEffect(() => {
    clearError()
    setResent(false)
  }, [pathname, clearError])

  // Somewhere sent the user here to sign in first -- approving a device, most
  // often. Returning to the default would lose what they were doing, and the
  // code in that URL cannot be typed back.
  const next = params.get('next')
  if (status === 'authenticated') {
    return <Navigate to={next && next.startsWith('/') ? next : SIGNED_IN_HOME} replace />
  }

  // The API's message for an unverified SSO account, matched loosely so a
  // wording tweak on that end does not silently drop the resend action.
  const needsVerification = (error ?? '').toLowerCase().includes('verify your email')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setResent(false)
    try {
      if (creating) await register(email, name, password)
      else await login(email, password)
    } catch {
      // The store surfaces the message; nothing else to do here.
    } finally {
      setSubmitting(false)
    }
  }

  const resend = async () => {
    setResending(true)
    try {
      await resendVerification(email)
      setResent(true)
    } catch {
      // The store surfaces the message; nothing else to do here.
    } finally {
      setResending(false)
    }
  }

  if (pendingVerificationEmail) {
    return (
      <AuthLayout
        {...PANEL.login}
        title="Check your email"
        subtitle={`We sent a verification link to ${pendingVerificationEmail}.`}
        assurance="Account created"
      >
        <p className="text-[14px] leading-relaxed text-brand-muted">
          Click the link in that email to verify your account, then come back here to sign in.
        </p>
        <Link
          to="/login"
          onClick={clearError}
          className="mt-5 flex w-full items-center justify-center rounded-md bg-brand py-3 text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      {...(creating ? PANEL.signup : PANEL.login)}
      title={creating ? 'Create your NevTan AI account' : 'Sign in to NevTan AI'}
      subtitle={
        creating
          ? 'Start running open models on the computer you already own.'
          : 'Get back to your conversations, models, and devices.'
      }
      assurance={creating ? 'Secure account setup' : 'Secure workspace sign-in'}
      footer={
        <p className="mt-5 text-center text-[14px] text-brand-muted">
          {creating ? 'Already have an account? ' : 'New to NevTan AI? '}
          <Link
            to={creating ? '/login' : '/signup'}
            className="font-extrabold text-brand transition hover:text-brand-dark"
          >
            {creating ? 'Sign in' : 'Create an account'}
          </Link>
        </p>
      }
      finePrint="NevTan AI runs open models on your own hardware. Answers are generated locally, and nothing is charged per message."
    >
      <form onSubmit={submit} className="space-y-5">
        {creating && (
          <Field
            label="Full name *"
            value={name}
            onChange={setName}
            autoComplete="name"
            icon={<User size={17} />}
            required
          />
        )}

        <Field
          label="Email *"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          icon={<Mail size={17} />}
          required
        />

        <div>
          <Field
            label="Password *"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            autoComplete={creating ? 'new-password' : 'current-password'}
            icon={<Lock size={17} />}
            required
            // Typing a password blind is a common cause of a failed sign-in;
            // letting people check it removes that entirely.
            adornment={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="rounded-md p-1.5 text-brand-muted transition hover:text-brand-heading"
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            }
          />
          {creating ? (
            <p className="mt-2 text-[12px] leading-relaxed text-brand-muted">
              Use at least 8 characters. A longer phrase is stronger than a short, complicated one.
            </p>
          ) : (
            <Link
              to="/forgot-password"
              className="mt-2 inline-block text-[13px] font-bold text-brand transition hover:text-brand-dark"
            >
              Forgot password?
            </Link>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-danger/25 bg-danger/[0.06] px-3 py-2.5 text-[13px] leading-relaxed text-danger"
          >
            <p>{error}</p>
            {needsVerification && (
              <button
                type="button"
                onClick={resend}
                disabled={resending || resent}
                className="mt-1.5 font-bold underline underline-offset-2 disabled:no-underline disabled:opacity-70"
              >
                {resent ? 'Verification email sent' : resending ? 'Sending…' : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-brand py-3 text-[16px] font-extrabold text-white shadow-[0_14px_28px_rgba(15,118,110,.24)] transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Please wait…' : creating ? 'Create account' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  )
}

/**
 * What the dark panel says on each route. Kept beside the form so the two
 * always change together.
 */
const FEATURES = [
  {
    icon: <Cpu size={17} />,
    title: 'Hardware aware',
    body: 'Models matched to what your computer can actually run.',
  },
  {
    icon: <ShieldCheck size={17} />,
    title: 'Answers stay local',
    body: 'Local models answer on your own computer.',
  },
  {
    icon: <Wallet size={17} />,
    title: 'No per-message cost',
    body: 'Free to use, with no subscription.',
  },
]

const PANEL = {
  login: {
    eyebrow: 'Local AI workspace',
    headline: (
      <>
        Your models and conversations,
        <br />
        on your own computer.
      </>
    ),
    lede: 'Your conversations, installed models, and paired devices in one place. NevTan AI runs open models on hardware you already own.',
    features: FEATURES,
  },
  signup: {
    eyebrow: 'Local AI onboarding',
    headline: (
      <>
        Run open AI models on
        <br />
        hardware you already own.
      </>
    ),
    lede: 'Create an account to see which models your computer can run, install one in a click, and start working locally.',
    features: FEATURES,
  },
}
